import React, { useState } from "react";
import { Button } from "primereact/button";
import { setupSchool, createCheckoutSession } from "../../../api/setupApi";
import { COUNTRY_MAP } from "../countries";

export default function ReviewStep({ formData, onNext, onBack, setSchoolId, toast }) {
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        schoolName: formData.schoolName,
        email: formData.email,
        phone: formData.phone,
        address: formData.address,
        country: formData.country,
        adminName: formData.adminName,
        adminEmail: formData.adminEmail,
        adminPassword: formData.adminPassword,
        sessionName: formData.sessionName,
        sessionStartDate: formData.sessionStartDate,
        sessionEndDate: formData.sessionEndDate,
        gradePreset: formData.gradePreset,
        planId: formData.planId || null,
        billingCycle: formData.billingCycle || null,
      };

      const result = await setupSchool(payload);

      if (result.success) {
        setSchoolId(result.school.id);

        if (result.requiresPayment) {
          // Paid plan — redirect to Stripe Checkout
          const checkout = await createCheckoutSession(
            result.school.id,
            formData.planId,
            formData.billingCycle || "monthly"
          );
          if (checkout.success && checkout.sessionUrl) {
            window.location.href = checkout.sessionUrl;
            return;
          }
        }

        // Free plan — go to provisioning step
        onNext();
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Setup Failed",
          detail: result.message || "Failed to set up school",
          life: 5000,
        });
      }
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: err.response?.data?.message || err.message || "An error occurred",
        life: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const gradeLabels = {
    "k-12": "K-12 (Kindergarten through Grade 12)",
    "1-10": "1-10 (Grade 1 through Grade 10)",
    "1-8": "1-8 (Grade 1 through Grade 8)",
    "other": "Other (Define Later)",
  };

  const formatDate = (d) => {
    if (!d) return "Default";
    return new Date(d).toLocaleDateString();
  };

  const isPaid = formData.selectedPlan && formData.selectedPlan.tier !== "free";

  return (
    <div>
      <div className="step-title">Review & Confirm</div>

      <div className="review-section">
        <h4>School Information</h4>
        <div className="review-row">
          <span className="review-label">School Name</span>
          <span className="review-value">{formData.schoolName}</span>
        </div>
        <div className="review-row">
          <span className="review-label">Email</span>
          <span className="review-value">{formData.email}</span>
        </div>
        {formData.country && (
          <div className="review-row">
            <span className="review-label">Country</span>
            <span className="review-value">
              {COUNTRY_MAP[formData.country]?.name || formData.country}
            </span>
          </div>
        )}
        {formData.phone && (
          <div className="review-row">
            <span className="review-label">Phone</span>
            <span className="review-value">
              {COUNTRY_MAP[formData.country]?.dialCode} {formData.phone}
            </span>
          </div>
        )}
        {formData.address && (
          <div className="review-row">
            <span className="review-label">Address</span>
            <span className="review-value">{formData.address}</span>
          </div>
        )}
      </div>

      <div className="review-section">
        <h4>Admin Account</h4>
        <div className="review-row">
          <span className="review-label">Admin Name</span>
          <span className="review-value">{formData.adminName}</span>
        </div>
        <div className="review-row">
          <span className="review-label">Admin Email</span>
          <span className="review-value">{formData.adminEmail}</span>
        </div>
      </div>

      <div className="review-section">
        <h4>Academic Configuration</h4>
        <div className="review-row">
          <span className="review-label">Session</span>
          <span className="review-value">
            {formData.sessionName || "Default"}
          </span>
        </div>
        <div className="review-row">
          <span className="review-label">Start Date</span>
          <span className="review-value">
            {formatDate(formData.sessionStartDate)}
          </span>
        </div>
        <div className="review-row">
          <span className="review-label">End Date</span>
          <span className="review-value">
            {formatDate(formData.sessionEndDate)}
          </span>
        </div>
        <div className="review-row">
          <span className="review-label">Grade Structure</span>
          <span className="review-value">
            {gradeLabels[formData.gradePreset] || formData.gradePreset}
          </span>
        </div>
      </div>

      {formData.selectedPlan && (
        <div className="review-section">
          <h4>Subscription Plan</h4>
          <div className="review-row">
            <span className="review-label">Plan</span>
            <span className="review-value">{formData.selectedPlan.name}</span>
          </div>
          {isPaid && (
            <div className="review-row">
              <span className="review-label">Billing</span>
              <span className="review-value">
                ${((formData.billingCycle === "annual"
                  ? formData.selectedPlan.annualPrice
                  : formData.selectedPlan.monthlyPrice) / 100).toFixed(0)}
                /{formData.billingCycle === "annual" ? "year" : "month"} after your trial
              </span>
            </div>
          )}
        </div>
      )}

      {/* Always shown: every self-serve signup starts on a free trial (the wizard
          has no plan step, so the server assigns the trial plan). */}
      <div className="review-section">
        <h4>Free trial</h4>
        <div className="review-row">
          <span className="review-label">Access</span>
          <span className="review-value">Full features for 14 days · no card needed</span>
        </div>
        <div className="review-row">
          <span className="review-label">After the trial</span>
          <span className="review-value">Choose a plan to keep going — your data stays safe</span>
        </div>
      </div>

      <div className="step-actions">
        <Button
          label="Back"
          icon="pi pi-arrow-left"
          className="p-button-secondary"
          onClick={onBack}
          disabled={submitting}
        />
        <Button
          label={
            submitting
              ? "Processing..."
              : "Start free trial"
          }
          icon="pi pi-check"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        />
      </div>
    </div>
  );
}
